// The rpg layer's screens on @rpg/ui (phase D1; decision 0046: screens on Preact, the screen stack, keyboard, pad and
// touch focus, semantic HTML with ARIA names; every text from the strings table): the inventory with its tabs and the
// equipment (the weapon and two charm slots), the skill tree per weapon, the shop (buy, sell), the forge and the
// cooking pot. They open from the pause menu (inventory, skills) and from the stations and NPCs in the world (shop,
// forge, pot: over the pause screen; back from them resumes play). Every action goes through the rpg layer's API
// (`Rpg`), applied at once to the sim's state. The shop's screen is @rpg-unit/shop's (`shopScreen`), given this
// game's string ids.
import { Button, Group, h, List, ListRow, Meter, Screen, Status, TabPanel, Tabs, type ScreenContext, type ScreenRender, type VNode } from "@rpg/ui";
import type { SpikeContent } from "../content";
import { shopScreen } from "@rpg-unit/shop";
import { ItemIcon } from "./icons";
import type { Rpg } from "./module";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;

/** What a station screen opened from the world gets: back resumes play. */
export interface StationParams {
  id: string;
  fromWorld?: boolean;
}

/** The screens' ids. */
export const RPG_SCREENS = { inventory: "inventory", skills: "skills", shop: "shop", forge: "forge", pot: "pot" } as const;

/** Build the screens over the rpg layer (read when each screen draws: the scene may rebuild it). */
export function rpgScreens(d: { rpg: () => Rpg | null; content: SpikeContent; resume: () => void }): Record<string, ScreenRender> {
  const reg = d.content.registry;
  const t = d.content.t;
  // each screen's last action result and its selected tab, kept while it is open (by its stack key)
  const status = new Map<number, string>();
  const tabs = new Map<number, string>();
  const why = (code: string, extra: Record<string, number> = {}): string => (code === "level" && extra.level ? t("refuse.minLevel", { level: extra.level }) : t(`refuse.${code}`));
  const itemName = (id: string): string => {
    const row = reg.get("items", id);
    return row ? t(row.name.id) : id;
  };
  const icon = (id: string): El | null => {
    const row = reg.get("items", id);
    return row ? ItemIcon({ shape: row.look.shape, colour: row.look.colour }) : null;
  };
  const needs = (inputs: readonly { item: string; count: number }[]): string => t("ui.needs", { list: inputs.map((i) => t("ui.itemLine", { item: itemName(i.item), count: i.count })).join(", ") });
  const act = (ctx: ScreenContext, result: string | null): void => {
    status.set(ctx.key, result ?? "");
    ctx.ui.refresh();
  };
  const backFor = (ctx: ScreenContext): (() => void) | undefined => {
    const p = ctx.params as StationParams | undefined;
    if (!p?.fromWorld) return undefined;
    // back resumes play: the flow's play clears the stack (this screen and the pause screen under it); the UI keeps
    // the back press from reaching the game's input, so resuming at once is safe
    return () => d.resume();
  };
  const gold = (r: Rpg): El => h("p", { class: "rpg-hint", "data-gold": String(r.state().wallet.balance) }, t("ui.gold", { amount: r.state().wallet.balance }));
  const need = (_ctx: ScreenContext): Rpg | null => d.rpg();

  const inventory: ScreenRender = (ctx) => {
    const r = need(ctx);
    if (!r) return h("div", null);
    const s = r.state();
    const set = r.abilities();
    const st = r.heroStats();
    const sel = tabs.get(ctx.key) ?? r.content.tabs[0]!.id;
    const stacks = r.inventory.tab(s.bag, sel);
    const use = r.inventory.tabUse(s.bag, sel);
    const statuses = st ? set.tags(st).flatMap((tag) => [...r.content.effectRows.values()].filter((e) => (e.grants ?? []).includes(tag))) : [];
    const charmSlots = r.content.slots.filter((x) => x.kind === "charm");
    return h(
      Screen,
      { ctx, title: t("ui.inventory"), dim: true },
      gold(r),
      st ? h("p", { class: "rpg-hint" }, t("ui.stats", { hp: Math.round(set.value(st, "hp")), hpMax: Math.round(set.value(st, "hpMax")), attack: Math.round(set.value(st, "attack")), defence: Math.round(set.value(st, "defence")) })) : null,
      statuses.length ? h("p", { class: "rpg-hint", "data-statuses": "" }, t("ui.statuses", { list: statuses.map((e) => t(e.name.id)).join(", ") })) : null,
      h("p", { class: "rpg-hint", "data-glyphs": String(r.glyphs()) }, t("ui.glyphs", { count: r.glyphs() })),
      h(
        Group,
        { id: "equipment", title: t("ui.equipment") },
        h(
          List,
          { id: "slots", label: t("ui.equipment") },
          r.content.slots.map((slot) => {
            const item = s.gear.slots[slot.id] ?? null;
            return h(
              ListRow,
              { id: `slot-${slot.id}`, title: t(slot.label), detail: item ? itemName(item) : t("ui.empty"), icon: item ? icon(item) : null },
              item && slot.kind !== "weapon" ? h(Button, { label: t("ui.unequip", { item: itemName(item) }), action: `unequip-${slot.id}`, onPress: () => (r.unequip(slot.id), act(ctx, null)) }) : null,
            );
          }),
        ),
      ),
      h(Tabs, { id: "inv", label: t("ui.inventory"), tabs: r.content.tabs.map((x) => ({ id: x.id, label: t(x.label) })), selected: sel, onSelect: (id) => (tabs.set(ctx.key, id), ctx.ui.refresh()) }),
      h(
        TabPanel,
        { id: "inv", selected: sel },
        h("p", { class: "rpg-hint", "data-tab-use": `${use.used}/${use.max}` }, t("ui.tabUse", use)),
        stacks.length
          ? h(
              List,
              { id: `tab-${sel}`, label: t(r.content.tabs.find((x) => x.id === sel)!.label) },
              stacks.map((stk, i) => {
                const def = r.content.items.get(stk.item);
                const row = r.content.itemRows.get(stk.item)!;
                const level = def.levels?.length ? r.equipment.level(s.gear, stk.item) : null;
                const worn = r.equipment.equipped(s.gear, stk.item) > 0;
                const actions: El[] = [];
                if (def.slot === "weapon" && !worn) actions.push(h(Button, { label: t("ui.equip", { item: itemName(stk.item) }), action: `equip-${stk.item}`, onPress: () => act(ctx, ((w) => (w ? why(w) : null))(r.equip("weapon", stk.item))) }));
                if (def.slot === "charm" && !worn)
                  for (const slot of charmSlots) actions.push(h(Button, { label: t("ui.equipTo", { item: itemName(stk.item), slot: t(slot.label) }), action: `equip-${stk.item}-${slot.id}`, onPress: () => act(ctx, ((w) => (w ? why(w) : null))(r.equip(slot.id, stk.item))) }));
                if (def.use?.length) actions.push(h(Button, { label: t("ui.use", { item: itemName(stk.item) }), action: `use-${stk.item}`, onPress: () => act(ctx, ((w) => (w ? why(w) : null))(r.use(stk.item))) }));
                return h(
                  ListRow,
                  {
                    id: `${stk.item}-${i}`,
                    title: level !== null ? t("ui.itemLevel", { item: itemName(stk.item), level }) : t("ui.itemLine", { item: itemName(stk.item), count: stk.count }),
                    ...(row.info ? { detail: t(row.info.id) } : {}),
                    ...(worn ? { badge: t("ui.equipped") } : {}),
                    icon: icon(stk.item),
                  },
                  actions.length ? actions : null,
                );
              }),
            )
          : h("p", { class: "rpg-hint" }, t("ui.nothing")),
      ),
      h(Status, { text: status.get(ctx.key) ?? "" }),
    );
  };

  const skills: ScreenRender = (ctx) => {
    const r = need(ctx);
    if (!r) return h("div", null);
    const p = r.progress();
    const weaponTree = r.content.trees.find((x) => x.weapon === r.content.weaponOf.get(r.weapon() ?? ""))?.id;
    const sel = tabs.get(ctx.key) ?? weaponTree ?? r.content.trees[0]!.id;
    const tree = r.content.trees.find((x) => x.id === sel)!;
    const def = r.content.progression.trees.find((x) => x.id === sel)!;
    const s = r.state();
    return h(
      Screen,
      { ctx, title: t("ui.skills"), dim: true },
      h(Meter, { id: "xp", label: t("ui.level", { level: p.level }), value: Number.isFinite(p.next) ? p.xp : 1, max: Number.isFinite(p.next) ? p.next : 1, valueText: Number.isFinite(p.next) ? t("ui.xp", { xp: p.xp, next: p.next }) : t("ui.xpMax") }),
      h("p", { class: "rpg-hint", "data-points": String(p.points) }, t("ui.points", { points: p.points })),
      h(Tabs, { id: "trees", label: t("ui.skills"), tabs: r.content.trees.map((x) => ({ id: x.id, label: t(x.name) })), selected: sel, onSelect: (id) => (tabs.set(ctx.key, id), ctx.ui.refresh()) }),
      h(
        TabPanel,
        { id: "trees", selected: sel },
        h(
          List,
          { id: `tree-${sel}`, label: t(tree.name) },
          def.nodes.map((n) => {
            const names = tree.nodes.get(n.id)!;
            const can = r.progression.canBuy(s.progress, sel, n.id);
            const owned = can === "owned";
            return h(
              ListRow,
              { id: `node-${sel}-${n.id}`, title: t(names.name), detail: t(names.info), ...(owned ? { badge: t("refuse.owned") } : can ? { badge: why(can, { level: n.minLevel ?? 0 }) } : {}) },
              owned ? null : h(Button, { label: t("ui.learn", { node: t(names.name), cost: n.cost }), action: `learn-${sel}-${n.id}`, disabled: can !== null, onPress: () => act(ctx, ((w) => (w ? why(w, { level: n.minLevel ?? 0 }) : t("ui.learned", { node: t(names.name) })))(r.learn(sel, n.id))) }),
            );
          }),
        ),
      ),
      h(Status, { text: status.get(ctx.key) ?? "" }),
    );
  };

  // the shop's screen is @rpg-unit/shop's, said in this game's strings
  const shop: ScreenRender = shopScreen({
    feature: () => d.rpg()?.shop ?? null,
    t: (id, vars) => t(id, vars),
    itemName,
    itemInfo: (id) => reg.get("items", id)?.info?.id ?? null,
    icon,
    count: (item) => ((r) => (r ? r.inventory.count(r.state().bag, item) : 0))(d.rpg()),
    equipped: (item) => ((r) => (r ? r.equipment.equipped(r.state().gear, item) > 0 : false))(d.rpg()),
    back: (ctx) => backFor(ctx),
    labels: { gold: "ui.gold", discount: "ui.discount", buyTab: "ui.buyTab", sellTab: "ui.sellTab", stockLeft: "ui.stockLeft", buy: "ui.buy", sell: "ui.sell", itemLine: "ui.itemLine", equipped: "ui.equipped", nothing: "ui.nothing", refused: (code) => `refuse.${code}` },
  });

  const station = (kind: "forge" | "pot"): ScreenRender => (ctx) => {
    const r = need(ctx);
    if (!r) return h("div", null);
    const s = r.state();
    const recipes = r.crafting.at(kind).filter((rc) => !("upgrade" in rc.output) || r.inventory.count(s.bag, rc.output.upgrade) > 0);
    // a forge shows each owned item's next level only (or that it is at its best)
    const shown = kind === "forge" ? recipes.filter((rc) => "upgrade" in rc.output && rc.output.level === r.equipment.level(s.gear, rc.output.upgrade) + 1) : recipes;
    const maxed = kind === "forge" ? [...r.content.weaponOf.keys()].filter((i) => r.inventory.count(s.bag, i) > 0 && r.equipment.level(s.gear, i) >= r.content.items.maxLevel(i)) : [];
    return h(
      Screen,
      { ctx, title: t(kind === "forge" ? "ui.forge" : "ui.pot"), dim: true, ...(backFor(ctx) ? { onBack: backFor(ctx)! } : {}) },
      gold(r),
      h(
        List,
        { id: `recipes-${kind}`, label: t(kind === "forge" ? "ui.forge" : "ui.pot") },
        shown.map((rc) => {
          const can = r.crafting.can(rc.id, { inv: s.bag, wallet: s.wallet, levels: s.gear.levels, station: kind });
          const out = "upgrade" in rc.output ? rc.output.upgrade : rc.output.items[0]!.item;
          const title = "upgrade" in rc.output ? t("recipe.forge", { item: itemName(out), level: rc.output.level }) : t(r.content.recipeNames.get(rc.id)!);
          const label = "upgrade" in rc.output ? t("ui.forgeTo", { item: itemName(out), level: rc.output.level, cost: rc.cost ?? 0 }) : t("ui.cook", { item: itemName(out) });
          return h(
            ListRow,
            { id: `recipe-${rc.id}`, title, detail: needs(rc.inputs), ...(can ? { badge: why(can) } : {}), icon: icon(out) },
            h(Button, { label, action: `craft-${rc.id}`, disabled: can !== null, onPress: () => act(ctx, ((x) => ("refused" in x ? why(x.refused) : null))(r.craft(rc.id, kind))) }),
          );
        }),
        maxed.map((i) => h(ListRow, { id: `max-${i}`, title: t("ui.forgeMax", { item: itemName(i) }), icon: icon(i) })),
      ),
      h(Status, { text: status.get(ctx.key) ?? "" }),
    );
  };

  return { inventory, skills, shop, forge: station("forge"), pot: station("pot") };
}
